import { useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { formatCivilDate, links, resolveFormattingLocale } from '@eis/core';
import { EmptyState } from '@eis/patterns';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { usePolicyPartnerHistory, usePolicyTimeline } from '../api/policies';
import { partnersDiffer } from '../model/partners';
import { isSuperseded, premiumWithPeriod, visibleSegments } from '../model/types';
import { PartnerHistoryPanel } from './PartnerHistoryPanel';

/**
 * The timeline — a read-side PROJECTION, never a stored object (decision S-6).
 *
 * It reads in the operations persona's words. There is no "version" on this screen: each row is a
 * period the policy was believed to look a certain way, and the ones a correction replaced are
 * hidden until somebody asks for them.
 *
 * Hidden, not deleted. Superseded rows are kept forever — GoBD and premium reproducibility need
 * every historical belief reconstructable — and the toggle is what makes that visible without
 * making every reader wade through it. Turning it on asks the service for them (`includeSuperseded`
 * is a separate auth scope), so a reader without that scope sees the toggle answer nothing rather
 * than a filtered lie.
 *
 * Each period is a LINK to the overview as of the day it starts (`?validAt=`): the overview is the
 * one screen that shows a belief in full — parties, premium, payload — and a read as of a date
 * is how the platform answers "what did this policy look like then" (SDD-008 §5). The row itself
 * says the period's monthly premium, so a change in price is visible without opening anything.
 */
export function PolicyTimelineTab({
  code,
  rules,
}: {
  readonly code: string;
  /** The pinned tarif's roles, for the names the partner history shows. */
  readonly rules?: readonly InstanceApi.PartnerRoleRule[];
}): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const [showSuperseded, setShowSuperseded] = useState(false);
  const timeline = usePolicyTimeline(code);
  const history = usePolicyPartnerHistory(code);

  const segments = visibleSegments(timeline.data?.segments, showSuperseded);

  return (
    <Stack spacing={2}>
      <FormControlLabel
        control={
          <Switch
            checked={showSuperseded}
            onChange={(event) => {
              setShowSuperseded(event.target.checked);
            }}
          />
        }
        label={t('policies.timeline.showSuperseded')}
      />

      {timeline.isPending ? (
        <Skeleton variant="rounded" height={180} />
      ) : segments.length === 0 ? (
        <EmptyState description={t('policies.timeline.empty')} />
      ) : (
        <Paper variant="outlined" component="section" sx={{ p: 2.5 }}>
          <Box
            component="ol"
            aria-label={t('policies.timeline.title')}
            sx={{ listStyle: 'none', m: 0, p: 0 }}
          >
            {segments.map((segment, index) => {
              const snapshot = segment.snapshot;
              const superseded = isSuperseded(segment);
              return (
                <Box
                  key={`${snapshot?.validFrom ?? ''}-${snapshot?.recordedAt ?? String(index)}`}
                  component="li"
                  sx={{
                    pl: 3,
                    pb: index === segments.length - 1 ? 0 : 2,
                    borderLeft: index === segments.length - 1 ? 0 : 1,
                    borderColor: 'divider',
                    ml: 0.75,
                    // Superseded rows are legible but visibly not the current belief. Colour is
                    // never the only carrier: the chip says it in words as well.
                    opacity: superseded ? 0.6 : 1,
                  }}
                >
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant="subtitle2" component="span">
                      {t('policies.timeline.period', {
                        from:
                          snapshot?.validFrom === undefined || snapshot.validFrom === ''
                            ? '—'
                            : formatCivilDate(snapshot.validFrom, locale),
                        to:
                          snapshot?.validTo === undefined || snapshot.validTo === ''
                            ? t('policies.timeline.open')
                            : formatCivilDate(snapshot.validTo, locale),
                      })}
                    </Typography>
                    {superseded && <Chip size="small" label={t('policies.timeline.superseded')} />}
                    {/* A segment whose PARTIES differ from the one before it (SDD-011 P-7). */}
                    {index > 0 &&
                      partnersDiffer(
                        segments[index - 1]?.policy?.partners,
                        segment.policy?.partners,
                      ) && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t('policies.timeline.partnersChanged')}
                          data-testid="partners-changed"
                        />
                      )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {t('policies.timeline.recordedAt', { at: snapshot?.recordedAt ?? '' })}
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ alignItems: 'baseline', mt: 0.5 }}>
                    {segment.policy?.premium?.monthly?.amount !== undefined && (
                      <Typography variant="body2" data-testid="period-premium">
                        {t('policies.timeline.premium', {
                          amount: premiumWithPeriod(segment.policy.premium, locale, (period) =>
                            t(`policies.premiumPeriod.${period}` as never, { defaultValue: '' }),
                          ),
                        })}
                      </Typography>
                    )}
                    {snapshot?.validFrom !== undefined && snapshot.validFrom !== '' && (
                      <Link
                        variant="body2"
                        href={links.insurance.policy(code, { validAt: snapshot.validFrom })}
                        onClick={(event) => {
                          event.preventDefault();
                          void navigate({ href: links.insurance.policy(code, { validAt: snapshot.validFrom }) });
                        }}
                        data-testid="period-open"
                      >
                        {t('policies.timeline.openAsOf', { date: formatCivilDate(snapshot.validFrom, locale) })}
                      </Link>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      <PartnerHistoryPanel history={history} rules={rules} locale={locale} />
    </Stack>
  );
}
